import api from '@/shared/utils/apiConfig';
import type { PrescriptionTemplateFormData } from '@/modules/prescription/types/prescriptionTemplate';
import type { PrescriptionTemplate } from '@/modules/prescription/types/prescriptionTemplate';

interface TemplateParams {
  [key: string]: string | number | boolean | undefined;
}

interface PaginatedTemplates {
  docs: PrescriptionTemplate[];
  totalDocs?: number;
  totalPages?: number;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
}

export type PrescriptionTemplatePayload = {
  name: string;
  description?: string;
  instructions?: string;
  notes?: string;
  isActive: boolean;
  medications: Array<{
    drug: string;
    quantity: number;
    usage?: string;
  }>;
};

export const ACTIVE_PRESCRIPTION_TEMPLATES_PARAMS = { isActive: true, limit: 1000 } as const;

export const prescriptionTemplateAPI = {
  getAll: async (params: TemplateParams = {}): Promise<ApiResponse<PaginatedTemplates>> => {
    const response = await api.get('/prescription-templates', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<PrescriptionTemplate>> => {
    const response = await api.get(`/prescription-templates/${id}`);
    return response.data;
  },

  create: async (data: PrescriptionTemplatePayload): Promise<ApiResponse<PrescriptionTemplate>> => {
    const response = await api.post('/prescription-templates', data);
    return response.data;
  },

  update: async (
    id: string,
    data: Partial<PrescriptionTemplatePayload>
  ): Promise<ApiResponse<PrescriptionTemplate>> => {
    const response = await api.put(`/prescription-templates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/prescription-templates/${id}`);
    return response.data;
  },
};

export const toTemplatePayload = (
  formData: PrescriptionTemplateFormData
): PrescriptionTemplatePayload => ({
  name: formData.name.trim(),
  description: formData.description.trim() || undefined,
  instructions: formData.instructions.trim() || undefined,
  notes: formData.notes.trim() || undefined,
  isActive: formData.isActive,
  medications: formData.medications.map((med) => ({
    drug: med.drug,
    quantity: med.quantity,
    usage: med.usage.trim() || undefined,
  })),
});
